import React, { useState, useEffect, useRef } from "react";
import Config from "../../Config";
import PropTypes from "prop-types";
import {
  CognitoUserPool,
  CognitoUserAttribute,
} from "amazon-cognito-identity-js";
import { connect } from "react-redux";
import { useTranslation, withTranslation } from "react-i18next";

import RegistrationForm from "./components/RegistrationForm/RegistrationForm";

import { TAXNUMBER_STATUS } from "LixaniAPI/enums";
import {
  setEmail,
  setPhone,
  setIsPhoneAccount,
} from "../../store/actions/user";
import { setRoute, ROUTE } from "../../store/actions/router";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";

import { passwordHelper } from "util/password";
import { password2Helper } from "util/password2";
import { setUserRegistrationActivity } from "services/set-user-registration-activity";
import { debounce } from "lodash";
import { useMemo } from "react";
import { fetchFromFinder } from "util/fetchFromFinder";
import { fetchBusinessInfo } from "util/fetchBusinessInfo";
import { parseCompanyData, parseStatus } from "util/helpers";
import { checkTaxNumber } from "services/check-tax-number";
import { phoneNormalization } from "util/phoneNormalization";
import { avatarUserSearch } from "util/avatarUserSearch";

const initialDateOfBirth = () => {
  let d = new Date();
  const currentYear = d.getFullYear();
  d.setFullYear(currentYear - 14);
  d.setMonth(0);
  d.setDate(1);
  return d.toISOString().slice(0, 10);
};

const maxDateOfBirth = () => {
  let d = new Date();
  const currentYear = d.getFullYear();
  d.setFullYear(currentYear - 13);
  d.setMonth(-1);
  d.setDate(-1);
  return d.toISOString().slice(0, 10);
};

const minDateOfBirth = () => {
  let d = new Date();
  const currentYear = d.getFullYear();
  d.setFullYear(currentYear - 120);
  return d.toISOString().slice(0, 10);
};

const initRegistrationData = {
  business_id: { value: "", required: true, error: false, type: "business_id" },
  city: { value: "", required: false, error: false },
  co: { value: "", required: false, error: false },
  contact_first_name: { value: "", required: false, error: false },
  contact_last_name: { value: "", required: false, error: false },
  contact_street1: { value: "", required: false, error: false },
  contact_street2: { value: "", required: false, error: false },
  contact_co: { value: "", required: false, error: false },
  contact_postcode: { value: "", required: false, error: false },
  contact_city: { value: "", required: false, error: false },
  contact_country: { value: "", required: false, error: false },
  contact_email: { value: "", required: false, error: false },
  contact_phone: { value: "", required: false, error: false },
  country: { value: "", required: false, error: false },
  dateOfBirth: {
    value: "",
    required: true,
    error: false,
    errorType: "",
    max: maxDateOfBirth(),
    min: minDateOfBirth(),
    type: "date",
  },
  dateOfBirthDay: {
    value: "",
    required: true,
    error: false,
    min: 1,
    max: 31,
  },
  dateOfBirthMonth: {
    value: "",
    required: true,
    error: false,
    min: 1,
    max: 12,
  },
  dateOfBirthYear: {
    value: "",
    required: true,
    error: false,
    min: new Date().getFullYear() - 120,
    max: new Date().getFullYear() - 13,
  },
  email: { value: "", required: false, error: false, type: "email" },
  explanation: { value: "", required: false, error: false },
  first_name: { value: "", required: true, error: false },
  last_name: { value: "", required: true, error: false },
  middle_name: { value: "", required: false, error: false },
  name: { value: "", required: true, error: false },
  password: {
    value: "",
    required: true,
    error: false,
    type: "password",
    check_same: "password2",
  },
  password2: {
    value: "",
    required: true,
    error: false,
    type: "password",
    is_same: "password",
  },
  phone: { value: "", required: true, error: false, type: "phone" },
  postcode: { value: "", required: false, error: false },
  prefill: { value: false, required: false, error: false },
  projectKey: { value: "", required: false, error: false, disabled: false },
  street1: { value: "", required: false, error: false },
  street2: { value: "", required: false, error: false },
  taxNumber: { value: "", required: true, error: false },
  employmentRelationship: {
    value: "",
    required: true,
    error: false,
  },
};

function RegistrationLogic(props) {
  const [cognitoUser, setCognitoUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stateError, setError] = useState(null);
  const [registered, setRegistered] = useState(false);
  const [registrationData, setRegistrationData] = useState({
    ...initRegistrationData,
  });
  const [taxNumberCheckedAt, setTaxNumberCheckedAt] = useState("");
  const [taxNumberStatus, setTaxNumberStatus] = useState(
    TAXNUMBER_STATUS.UNCHECKED
  );
  const [userConfirmed, setUserConfirmed] = useState(false);
  const [tradeRegisterStatus, setTradeRegisterStatus] = useState("no");
  const [businessIdCountry, setBusinessIdCountry] = useState("finland");
  const [businessIdStatus, setBusinessIdStatus] = useState(false);
  const [foundByBusinessId, setFoundByBusinessId] = useState(false);
  const [validCompany, setValidCompany] = useState(false);
  const [companyData, setCompanyData] = useState(null);
  const [handleBossCheckboxValue, setBossValue] = useState("");
  const [dataFoundByTaxnumberCheck, setDataFoundByTaxNumberCheck] =
    useState(false);
  const { t, i18n } = useTranslation("common");

  const [activityId, setActivityId] = useState(uuidv4());
  const hasUserInteraction = useRef(false);
  const isFirstRender = useRef(true);

  const [taxNumberLoading, setTaxNumberLoading] = useState(false);

  const registrationActivity = useMemo(() => {
    return {
      id: activityId,
      phone: registrationData.phone.value,
      projectKey: registrationData.projectKey.value,
      first_name: registrationData.first_name.value,
      last_name: registrationData.last_name.value,
      dateOfBirth: registrationData.dateOfBirth.value,
      taxNumber: registrationData.taxNumber.value,
      tradeRegisterStatus: tradeRegisterStatus,
      businessIdStatus: businessIdStatus,
      businessIdCountry: businessIdCountry,
      business_id: registrationData.business_id.value,
      name: registrationData.name.value,
      status: "INCOMPLETE",
      type: "EMPLOYEE",
      employmentRelationship: registrationData.employmentRelationship.value,
    };
  }, [registrationData, tradeRegisterStatus]);

  const debouncedSaveActivity = useRef(
    debounce(async (data) => {
      await setUserRegistrationActivity(data);
    }, 500)
  ).current;

  const handlePhoneValidationBlur = async (phonenumber) => {
    const validatedPhone = phoneNormalization(phonenumber);
    props.onSetPhone(validatedPhone);

    const avatarUsers = await avatarUserSearch(validatedPhone, 8);

    if (avatarUsers.length === 1) {
      const dateOfBirth = avatarUsers[0].dateOfBirth
        ? moment(avatarUsers[0].dateOfBirth).format("YYYY-MM-DD")
        : "";

      const dateOfBirthArray = dateOfBirth.split("-");

      setRegistrationData((prevState) => ({
        ...prevState,
        dateOfBirth: {
          ...prevState.dateOfBirth,
          value: dateOfBirth,
        },
        dateOfBirthDay: {
          ...prevState.dateOfBirthDay,
          value: parseInt(dateOfBirthArray[2], 10),
        },
        dateOfBirthMonth: {
          ...prevState.dateOfBirthMonth,
          value: parseInt(dateOfBirthArray[1], 10),
        },
        dateOfBirthYear: {
          ...prevState.dateOfBirthYear,
          value: dateOfBirthArray[0],
        },
        taxNumber: {
          ...prevState.taxNumber,
          value: avatarUsers[0].taxNumber,
        },
        first_name: {
          ...prevState.first_name,
          value: avatarUsers[0].firstName,
        },
        last_name: {
          ...prevState.last_name,
          value: avatarUsers[0].lastName,
        },
        phone: {
          ...prevState.phone,
          value: validatedPhone,
        },
      }));
    } else {
      setRegistrationData((prevState) => ({
        ...prevState,
        phone: {
          ...prevState.phone,
          value: validatedPhone,
        },
      }));
    }
  };

  const handleSaveActivity = async (dateofBirth) => {
    if (isFirstRender.current) {
      setTimeout(() => {
        isFirstRender.current = false;
      }, 1000);
      return;
    }

    if (hasUserInteraction.current) {
      if (!dateofBirth) {
        debouncedSaveActivity(registrationActivity);
      } else {
        const updatedActivity = {
          ...registrationActivity,
          dateOfBirth: dateofBirth,
        };
        debouncedSaveActivity(updatedActivity);
      }
    }
  };

  useEffect(() => {
    const initPhone = () => {
      let prefill = "";

      // callback if not signed in.
      try {
        prefill = decodeURIComponent(
          new URL(window.location.href).search.split("?callback=")
        ).split("?data=");
      } catch (err) {
        prefill = new URL(window.location.href).search.split("?data=");
      }

      let prefilldata = {};
      if (prefill.length > 1) {
        try {
          let d = JSON.parse(atob(prefill[1]));
          prefilldata = Object.keys(d).reduce((resp, key) => {
            resp[key] = { ...initRegistrationData[key], value: d[key] };
            return resp;
          }, {});
          prefilldata["prefill"] = {
            value: d.prefill ? d.prefill.value : true,
            required: false,
            error: false,
          };
        } catch (err) {
          console.log(err);
        }
      }

      if (!props.phone && prefilldata["phone"]) {
        props.onSetPhone(prefilldata["phone"].value);
      }

      return prefilldata["phone"] || "";
    };

    const initProjectKey = () => {
      let projectKey = "";
      let prefill = "";
      let joinUrl = "";

      // callback if not signed in.
      try {
        prefill = decodeURIComponent(
          new URL(window.location.href).search.split("?callback=")
        ).split("?data=");
      } catch (err) {
        prefill = new URL(window.location.href).search.split("?data=");
      }

      try {
        const url = new URL(window.location.href).search.split("?callback=");
        if (url && url.length > 1 && url[1]) {
          console.log("Project join URL detected");
          joinUrl = decodeURIComponent(url[1]);
          if (joinUrl && joinUrl.includes("/project/join/")) {
            const urlParts = joinUrl.split("/project/join/");
            if (urlParts.length > 1) {
              const projectKey = urlParts[1].split("/")[0];

              // Check if projectKey is valid
              if (projectKey && projectKey.trim() !== "") {
                setRegistrationData((prevState) => ({
                  ...prevState,
                  projectKey: {
                    ...prevState.projectKey,
                    value: projectKey,
                    disabled: true,
                  },
                }));
              } else {
                console.log("Invalid or empty project key extracted");
              }
            }
          }
        }
      } catch (error) {
        console.log("Error parsing URL:", error.message);
      }

      if (prefill.length > 1) {
        try {
          let d = JSON.parse(atob(prefill[1]));
          setRegistrationData((prevState) => {
            return {
              ...prevState,
              projectKey: {
                ...prevState.projectKey,
                value: d.project ? d.project.projectKey : "",
                disabled: true,
              },
            };
          });
        } catch (err) {
          console.log(err);
        }
      }

      return projectKey;
    };

    initPhone();
    initProjectKey();
    /*setRegistrationData({
          ...registrationData,
          boss: { ...handleBossCheckboxValue },
        })
        */
  }, []);

  const register = async () => {
    setError(null);
    setLoading(true);
    let test = { ...registrationData["boss"] };

    console.log(test, "registerAsync sisällä handlebossvalue");
    /*setRegistrationData({
      ...registrationData,
      boss: { ...handleBossCheckboxValue },
    })
    */
    let userPool = new CognitoUserPool({
      UserPoolId: Config.COGNITO_USER_POOL_ID,
      ClientId: Config.COGNITO_CLIENT_ID,
    });

    const data = { ...registrationData };
    const email = props.email.toLowerCase();
    const phone = props.phone.replace(/[^a-zA-Z0-9+ ]/g, "");
    //data.phone.value; //props.phone;
    const username = props.isPhoneAccount ? phone : email;

    let attributeList = [];
    attributeList.push(
      new CognitoUserAttribute({ Name: "email", Value: email })
    );
    attributeList.push(
      new CognitoUserAttribute({
        Name: "phone_number",
        Value: data.phone.value.replace(/[^a-zA-Z0-9+ ]/g, ""),
      })
    );
    attributeList.push(
      new CognitoUserAttribute({
        Name: "given_name",
        Value: data.first_name.value,
      })
    );
    attributeList.push(
      new CognitoUserAttribute({
        Name: "family_name",
        Value: data.last_name.value,
      })
    );

    //    if (data.middle_name.value) {
    //      attributeList.push(new CognitoUserAttribute({ Name: 'middle_name', Value: data.middle_name.value }));
    //    }

    if (data.projectKey.value && data.projectKey.value.length > 2) {
      addProjectKeyToUrl();
    }

    const reddata = Object.keys(data)
      .filter(
        (key) =>
          ![
            "project",
            "password",
            "password2",
            "email",
            "phone",
            "first_name",
            "last_name",
            "middle_name",
            "boss",
            "projectKey",
            "dateOfBirthDay",
            "dateOfBirthMonth",
            "dateOfBirthYear",
          ].includes(key)
      )
      .reduce((resp, key) => {
        const ob = data[key];
        if (ob.value) {
          resp[key] = ob.value;
        }
        return resp;
        //			({[key]:data[key].value})
      }, {});

    if (handleBossCheckboxValue !== "") {
      reddata["boss"] = handleBossCheckboxValue;
    }
    //-- Add taxNumber check date and status
    if (taxNumberCheckedAt) {
      reddata["taxNumberCheckedAt"] = taxNumberCheckedAt;
      reddata["taxNumberStatus"] = taxNumberStatus;
    }

    attributeList.push(
      new CognitoUserAttribute({
        Name: "custom:employee_base64",
        Value: btoa(JSON.stringify(reddata)),
      })
    );

    //-- Add locale attribute
    let locale = i18n.language;
    if (!locale) {
      locale = navigator.language || navigator.userLanguage;
    }
    locale = locale.split("-")[0];
    attributeList.push(
      new CognitoUserAttribute({ Name: "locale", Value: locale })
    );

    let result = null;
    try {
      result = await new Promise((resolve, reject) => {
        userPool.signUp(
          username,
          data.password.value,
          attributeList,
          null,
          (error, result) => {
            if (error) reject(error);
            else resolve(result);
          }
        );
      });
    } catch (error) {
      setLoading(false);
      console.error("Registration error", JSON.stringify(error));
      if (error.code === "UsernameExistsException") {
        setError(props.t("error.already_registered"));
      } else if (error.message === "Invalid phone number format.") {
        setError(props.t("error.invalid_phone_format"));
      } else {
        setError(error.message);
      }
      return;
    }

    setCognitoUser(result.user);
    setRegistered(true);
    setRegistrationData({ ...initRegistrationData });
    setUserConfirmed(result.userConfirmed);
  };

  const addProjectKeyToUrl = () => {
    const currentUrl = new URL(window.location.href);
    const hasDataParam = currentUrl.search.includes("?data=");

    if (!hasDataParam) {
      const dataToEncode = {
        project: {
          projectKey: registrationData.projectKey.value,
        },
      };

      // Encode the data
      const encodedData = btoa(JSON.stringify(dataToEncode));

      // Add to URL
      // No parameters yet, add data parameter
      currentUrl.search = `?data=${encodedData}`;
    }

    // Update the browser URL without reloading the page
    window.history.pushState({}, "", currentUrl.toString());
    console.log("Added projectKey to URL:", currentUrl.toString());
  };

  const handleDateOfBirthYearOnBlur = (name) => (event) => {
    if (name === "dateOfBirthYear") {
      const yearValue = event.target.value;

      if (yearValue && yearValue.length !== 4) {
        setRegistrationData((prevState) => ({
          ...prevState,
          dateOfBirthYear: {
            ...prevState.dateOfBirthYear,
            error: true,
          },
          dateOfBirth: {
            ...prevState.dateOfBirth,
            error: true,
          },
        }));
      } else {
        setRegistrationData((prevState) => ({
          ...prevState,
          dateOfBirthYear: {
            ...prevState.dateOfBirthYear,
            error: false,
          },
          dateOfBirth: {
            ...prevState.dateOfBirth,
            error: false,
          },
        }));
      }
    }
  };

  const handleChangeData = (name) => (event) => {
    let data = { ...registrationData[name] };
    let value = event;
    try {
      value = event.target.value;
    } catch (err) {
      //console.log(err);
    }

    // User interaction has happened
    hasUserInteraction.current = true;

    if (name === "phone") {
      value = value.replace(/ /g, "");
    }
    data.value = value;

    if (data.type === "email") {
      props.onSetEmail(value); //event.target.value);
    } else if (data.type === "phone") {
      props.onSetPhone(value); //event.target.value);
      //props.onSetPhone(event);
    }

    if (name === "taxNumber") {
      value = value.replace(/[^0-9]/g, "");
      data.value = value;
    }

    if (
      name === "dateOfBirthDay" ||
      name === "dateOfBirthMonth" ||
      name === "dateOfBirthYear"
    ) {
      // Use functional state update to ensure we have the latest state
      setRegistrationData((prevState) => {
        // Create updated copy of the specific field
        const updatedField = { ...prevState[name], value: value };

        // Get the most current values using prevState
        const day =
          name === "dateOfBirthDay" ? value : prevState.dateOfBirthDay.value;
        const month =
          name === "dateOfBirthMonth"
            ? value
            : prevState.dateOfBirthMonth.value;
        const year =
          name === "dateOfBirthYear" ? value : prevState.dateOfBirthYear.value;

        // Create a copy of the entire state with the updated field
        const updatedState = { ...prevState, [name]: updatedField };

        // Only update the combined date if all components have values
        if (day && month && year) {
          const paddedDay = day.toString().padStart(2, "0");
          const paddedMonth = month.toString().padStart(2, "0");
          const dateString = `${year}-${paddedMonth}-${paddedDay}`;

          console.log(" dateString:", dateString);
          // Validate the date
          let dateError = false;
          const dateValue = moment(dateString);
          console.log(" dateValue:", dateValue);

          if (!dateValue.isValid()) {
            dateError = true;
            const maxDaysInMonth = moment(
              `${year}-${paddedMonth}`
            ).daysInMonth();

            if (parseInt(day) > maxDaysInMonth) {
              updatedState.dateOfBirthDay = {
                ...updatedState.dateOfBirthDay,
                error: true,
              };
            }
          } else {
            dateError = false;
            updatedState.dateOfBirthDay = {
              ...updatedState.dateOfBirthDay,
              error: false,
            };
            // Save date string for API calls after state update is complete
            setTimeout(() => {
              handleSaveActivity(dateString);
            }, 0);
          }

          updatedState.dateOfBirth = {
            ...prevState.dateOfBirth,
            value: dateString,
            error: dateError,
          };
        }

        return updatedState;
      });

      return;
    }

    data.error = !dataValid(data)(true);

    setRegistrationData((prevState) => ({
      ...prevState,
      [name]: data,
    }));
  };

  const dataValid = (data) => (check) => {
    let value = data.value;

    if (
      data.type === "date" ||
      ["dateOfBirthDay", "dateOfBirthMonth", "dateOfBirthYear"].includes(
        data.name
      )
    ) {
      if (data.error) {
        return false;
      }
      return true;
    }

    if (data.type === "business_id" && data.error === true) {
      return false;
    }

    const isEmpty = typeof value !== "string" ? !value : value.trim() === "";

    if (data.required && isEmpty) {
      return false;
    }

    if (data.is_same) {
      if (
        password2Helper(props.t)(
          registrationData[data.is_same].value,
          value
        ) !== null
      )
        return false;
    } else {
      if (data.type === "password" && passwordHelper(props.t)(value) !== null) {
        return false;
      } else if (check && data.type === "business_id") {
        console.log("BUSINESS ID CHECK");
        // check y-tunnus
        let ytunnus = "0000000" + data.value.match(/\d/g).join("");
        ytunnus = ytunnus.substr(ytunnus.length - 8, 8).split("");
        let cr1 = parseInt(ytunnus.pop());

        const c = ytunnus.reduce(
          (resp, ch) => {
            const i = parseInt(ch);
            const k = resp.shift();
            resp[resp.length - 1] += i * k;
            return resp;
          },
          [7, 9, 10, 5, 8, 4, 2, 0]
        );
        const cr2 = c % 11 === 0 ? 0 : 11 - (c % 11);

        // y-tunnus ok. check sv
        if (cr1 === cr2) {
          const url =
            "https://selvitysvelvollisuus.fi:9000/svapi/company-info/";
          //	const url='https://dev.selvitysvelvollisuus.fi:9000/svapi/company-info/';
          fetch(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ business_id: data.value }),
          })
            .then((res) => res.json())
            .then((x) => {
              console.log("löytyi", x);
              const d = { ...registrationData["name"] };
              d.value = x.trade_name;

              setRegistrationData({
                ...registrationData,
                ["name"]: d || "",
              });
            })
            .catch((res) => {
              console.log(res);
            });
        }
      }
    }
    return true;
  };

  const handleBusinessIdCountryRadioChange = (e) => {
    setBusinessIdCountry(e.target.value);
    setFoundByBusinessId(false);
  };

  /*useEffect(() =>
  {
     setRegistrationData({
        ...registrationData,
        boss:{handleBossCheckboxValue}
        //boss: { ...businessIdData.value },
      })
  }) */
  const handleBossCheckbox = (e) => {
    let businessIdData = { ...registrationData["business_id"] };

    if (e.target.checked === true) {
      setBossValue(businessIdData.value);
      console.log("true", handleBossCheckboxValue);
    }
    if (e.target.checked === false) {
      setBossValue("");
      console.log("false", handleBossCheckboxValue);
    }
  };

  /*   if(handleBossCheckboxValue != '') {
     setRegistrationData({
       ...registrationData,
       boss: { ...handleBossCheckboxValue },
     })
   }*/
  //console.log(e.target.checked,"handleBossCheckbox")
  //setBossValue(e.target.value);
  //console.log(handleBossCheckboxValue,"handlebossCheckboxvalue")

  /*useEffect(() => {
      setRegistrationData({
      ...registrationData,
      boss: { ...handleBossCheckboxValue },
    })
    console.log("handlebosscheckbox",handleBossCheckboxValue)
    })
    */
  useEffect(() => {
    if (handleBossCheckboxValue !== "") {
      console.log(handleBossCheckboxValue, "Setregisteriintämä");
      setRegistrationData({
        ...registrationData,
        boss: { handleBossCheckboxValue },
      });
    }
  }, [handleBossCheckboxValue]);

  const handleChangeBusinessId = (event) => {
    let businessIdData = { ...registrationData["business_id"] };
    let nameData = { ...registrationData["name"] };
    let businessIdValue = event;
    try {
      businessIdValue = event.target.value;

      if (businessIdValue.length === 0) {
        setFoundByBusinessId(false);
        nameData.value = "";
        businessIdData.value = "";
        setRegistrationData((prevState) => ({
          ...prevState,
          business_id: { ...businessIdData },
          name: { ...nameData },
        }));
        return;
      }

      if (businessIdCountry === "finland") {
        // Only allow numbers and dashes in businessId
        businessIdValue = businessIdValue.replace(/[^0-9-]/g, "");

        if (businessIdValue.length > 9) {
          businessIdValue = businessIdValue.substring(0, 9);
        }

        // If businessId either 8 numbers or 7 numbers and a dash followed by a number
        if (
          /^\d{7}-\d{1}$/.test(businessIdValue) ||
          /^\d{8}$/.test(businessIdValue)
        ) {
          if (/^\d{8}$/.test(businessIdValue)) {
            // add dash between 7th and 8th digit
            businessIdValue = businessIdValue.replace(/(\d{7})(\d)/, "$1-$2");
          }

          fetchBusinessInfo(businessIdValue).then((data) => {
            console.log("fetchBusinessInfo data:", data);
            if (!data || !data.businessName) {
              console.log("business not found from fetchBusinessInfo");
              return;
            }

            setFoundByBusinessId(true);
            const companyData = parseCompanyData(data);
            setCompanyData(companyData);

            const status = parseStatus(data);

            if (status.status === "REJECTED") {
              businessIdData.error = true;
              businessIdData.errorMessage = t(
                `error.business_id.${status.rejectionReason.toLowerCase()}`
              );
              setBusinessIdStatus(status.rejectionReason);
              setTradeRegisterStatus(status.tradeRegisterStatus || "no");
              setRegistrationData((prevState) => ({
                ...prevState,
                business_id: { ...businessIdData },
                name: { ...nameData },
              }));
              return;
            } else {
              setBusinessIdStatus(status.status);
              setTradeRegisterStatus(status.tradeRegisterStatus || "no");
              businessIdData.error = false;
              businessIdData.errorMessage = "";

              nameData.value = data.businessName;
              nameData.error = false;
              setValidCompany(true);

              setRegistrationData((prevState) => ({
                ...prevState,
                business_id: { ...businessIdData },
                name: { ...nameData },
              }));
            }
          });

          // NOTE: CAN BE ADDED FOR BACKUP, BUT THERE SHOULD BE NO REASON WHY THE PAID VERSION USED IN
          // fetchBusinessInfo WOULD NOT FIND THE COMPANY and THE FREE VERSION WOULD FIND IT (for example: free version does not provide toiminimiyrittäjät)
          // const url = `https://avoindata.prh.fi/opendata-ytj-api/v3/companies?businessId=${businessIdValue}`;
          // console.log("url", url);
          // //curl -X GET --header 'Accept: application/json' 'https://avoindata.prh.fi/opendata-ytj-api/v3/companies?businessId=${businessIdValue}'
          // // fetch using the above curl command
          // fetch(url, {
          //   headers: {
          //     Accept: "application/json",
          //   },
          // })
          //   .then((res) => res.json())
          //   .then((x) => {
          //     console.log("x", x);
          //     if (x.companies.length > 0 && x.companies[0].names && x.companies[0].names[0].name) {
          //       nameData.value = x.companies[0].names[0].name;
          //       nameData.error = false;

          //       const company = x.companies[0];

          //       setFoundByBusinessId(true);
          //       if (company.status === '2' && company.tradeRegisterStatus === '1') {
          //         setTradeRegisterStatus('yes');
          //         setBusinessIdStatus('ACTIVE');
          //       } else if (company.status === '5') {
          //         setBusinessIdStatus('REMOVED');
          //         businessIdData.error = true;
          //       } else if (company.status === '1') {
          //         setBusinessIdStatus('PENDING');
          //         businessIdData.error = true;
          //       }

          //       setRegistrationData((prevState) => ({
          //         ...prevState,
          //         business_id: { ...businessIdData },
          //         name: { ...nameData },
          //       }));

          //     } else {
          //       // console.log("not found from avoin data, trying finder");
          //       // fetchFromFinder({ businessIdValue }).then((response) => {
          //       //   if (!response || !response.companyName) {
          //       //     console.log("business not found from finder");
          //       //   } else {
          //       //     nameData.value = response.companyName;
          //       //     nameData.error = false;
          //       //     businessIdData.value = businessIdValue;
          //       //     setFoundByBusinessId(true);
          //       //     setTradeRegisterStatus('unknown');
          //       //     setBusinessIdStatus('x');

          //       //     setRegistrationData((prevState) => ({
          //       //       ...prevState,
          //       //       business_id: { ...businessIdData },
          //       //       name: { ...nameData },
          //       //     }));
          //       //   }
          //       // })
          //     }
          //   })
          //   .catch((res) => {
          //     console.log(res);
          //   });
        }
      }
    } catch (err) {
      //console.log(err);
    }

    // const fetchFromFinder = () => {
    //   const url = `https://www.finder.fi/search?what=${businessIdValue}`;

    //   fetch(url, {
    //     headers: {
    //       Accept: "application/json",
    //     },
    //   })
    //     .then((res) => res.json())
    //     .then((x) => {
    //       console.log("fetchFromFinder x:", x)
    //       if (x.results.length > 0 && x.results[0].name) {
    //         nameData.value = x.results[0].name;
    //         setRegistrationData({
    //           ...registrationData,
    //           business_id: { ...businessIdData },
    //           name: { ...nameData },
    //         });
    //       } else {
    //         console.log("business not found");
    //       }
    //     })
    //     .catch((res) => {
    //       console.log(res);
    //     });
    // };

    // const fetchFromAvoinDataBis = () => {
    //   const url = `https://avoindata.prh.fi/opendata/bis/v1/${businessIdValue}`;

    //   fetch(url, {
    //     headers: {
    //       Accept: "application/json",
    //     },
    //   })
    //     .then((res) => res.json())
    //     .then((x) => {
    //       if (x.results.length > 0 && x.results[0].name) {
    //         nameData.value = x.results[0].name;
    //         setRegistrationData((prevState) => ({
    //           ...prevState,
    //           business_id: { ...businessIdData },
    //           name: { ...nameData },
    //         }));
    //       } else {
    //         // fetchFromFinder();
    //         console.log("business not found");
    //       }
    //     })
    //     .catch((res) => {
    //       console.log(res);
    //     });
    // };

    businessIdData.value = businessIdValue;

    setRegistrationData((prevState) => ({
      ...prevState,
      business_id: { ...businessIdData },
      name: { ...nameData },
    }));

    setTimeout(() => handleSaveActivity(), 100);
  };

  const lambdaCheckTaxNumber = async () => {
    let taxNumberError = false;
    let taxNumberChecked = false;
    let taxNumberErrorMessage = "";

    setTaxNumberLoading(true);

    setRegistrationData((prevState) => ({
      ...prevState,
      taxNumber: { ...registrationData.taxNumber, error: taxNumberError },
    }));
    const dateOfBirth = registrationData.dateOfBirth.value;
    const taxNumber = registrationData.taxNumber.value;
    if (taxNumber.length === 0) {
      setTaxNumberLoading(false);
      return;
    }

    const variables = { taxNumber, dateOfBirth };

    checkTaxNumber(variables)
      .then((res) => {
        if (res.status === 200) {
          const message = res.data;
          if (message.InfoExists === false) {
            taxNumberError = true;
            taxNumberErrorMessage = t("auth.tax_number_error_or_inactive");
          } else if (message.TaxNumberMatchBirthday === false) {
            taxNumberError = true;
            taxNumberErrorMessage = t("auth.tax_number_birthday_mismatch");
          }

          // Auto input first and last name if available
          if (message.FirstName && message.LastName) {
            // Split FirstName to separate first name
            const nameParts = message.FirstName.trim().split(" ");
            const firstName = nameParts[0] || "";

            setRegistrationData((prevState) => ({
              ...prevState,
              first_name: {
                ...prevState.first_name,
                // Only update if field is empty
                value:
                  prevState.first_name.value.trim() === ""
                    ? firstName
                    : prevState.first_name.value,
              },
              last_name: {
                ...prevState.last_name,
                // Only update if field is empty
                value:
                  prevState.last_name.value.trim() === ""
                    ? message.LastName
                    : prevState.last_name.value,
              },
            }));
            setDataFoundByTaxNumberCheck(true);
          }
          taxNumberChecked = true;
        } else if (res.status === 400) {
          taxNumberError = true;
          taxNumberChecked = true;
          if (res.data.ErrorCode === "1005") {
            taxNumberErrorMessage = t("auth.tax_number_too_short");
          }
        } else if (res.status === 404 || res.status === 500) {
          if (res.data.ErrorCode) {
            console.log(res.data.ErrorCode);
            console.log(res.data.ErrorText);
          }
        }
        if (taxNumberChecked) {
          console.log("taxNumberChecked", taxNumberChecked);
          setTaxNumberCheckedAt(new Date().toISOString());
          setTaxNumberStatus(
            taxNumberError ? TAXNUMBER_STATUS.INVALID : TAXNUMBER_STATUS.VALID
          );
        }

        setRegistrationData((prevState) => ({
          ...prevState,
          taxNumber: {
            ...registrationData.taxNumber,
            error: taxNumberError,
            errorMessage: taxNumberErrorMessage,
          },
        }));

        setTaxNumberLoading(false);

        setTimeout(() => handleSaveActivity(), 100); // save after small delay to ensure state is updated
      })
      .catch((error) => {
        // Also set loading to false on error
        setTaxNumberLoading(false);
        console.error("Tax number check error:", error);
      });

    const url = Config.CHECKTAXNUMBER_URL;
    const apikey = Config.CHECKTAXNUMBER_APIKEY;
    // fetch(url, {
    //   method: "POST",
    //   headers: {
    //     "Content-Type": "application/json",
    //     "x-api-key": apikey,
    //   },
    //   body: JSON.stringify(variables),
    // })
    //   .then((res) => res.json())
    //   .then((x) => {
    //     const statusCode = x[0];
    //     const message = JSON.parse(x[1]);
    //     console.log(statusCode);
    //     console.log(message);
    //     if (statusCode === 200) {
    //       if (message.InfoExists === false) {
    //         taxNumberError = true;
    //         taxNumberErrorMessage = t("auth.tax_number_error_or_inactive");
    //       } else if (message.TaxNumberMatchBirthday === false) {
    //         taxNumberError = true;
    //         taxNumberErrorMessage = t("auth.tax_number_birthday_mismatch");
    //       }
    //       // Auto input first and last name if available
    //       if (message.FirstName && message.LastName) {
    //         setRegistrationData((prevState) => ({
    //           ...prevState,
    //           first_name: {
    //             ...prevState.first_name,
    //             value: message.FirstName,
    //           },
    //           last_name: {
    //             ...prevState.last_name,
    //             value: message.LastName,
    //           },
    //         }));
    //       }
    //       taxNumberChecked = true;
    //     } else if (statusCode === 400) {
    //       taxNumberError = true;
    //       taxNumberChecked = true;
    //       if (message.ErrorCode === "1005") {
    //         taxNumberErrorMessage = t("auth.tax_number_too_short");
    //       }
    //     } else if (statusCode === 404 || statusCode === 500) {
    //       if (message.ErrorCode) {
    //         console.log(message.ErrorCode);
    //         console.log(message.ErrorText);
    //       }
    //     }
    //     if (taxNumberChecked) {
    //       console.log("taxNumberChecked", taxNumberChecked);
    //       setTaxNumberCheckedAt(new Date().toISOString());
    //       setTaxNumberStatus(
    //         taxNumberError ? TAXNUMBER_STATUS.INVALID : TAXNUMBER_STATUS.VALID
    //       );
    //     }

    //     setRegistrationData((prevState) => ({
    //       ...prevState,
    //       taxNumber: {
    //         ...registrationData.taxNumber,
    //         error: taxNumberError,
    //         errorMessage: taxNumberErrorMessage,
    //       },
    //     }));
    //   })
    //   .catch((res) => console.log(res));
  };

  const togglePhoneAccount = () => {
    const isPhoneAccount = !props.isPhoneAccount;
    setRegistrationData((prevState) => ({
      ...prevState,
      email: { ...registrationData.email, required: !isPhoneAccount },
      phone: { ...registrationData.phone, required: isPhoneAccount },
    }));

    props.onSetIsPhoneAccount(isPhoneAccount);
  };

  let content = null;
  let formValid = Object.entries(registrationData).every((d) =>
    dataValid(d[1])(false)
  );

  if (!registered) {
    content = (
      <RegistrationForm
        data={registrationData}
        email={props.email}
        error={stateError}
        isPhoneAccount={props.isPhoneAccount}
        isValid={dataValid(true)}
        loading={loading}
        phone={props.phone}
        onBlurTaxNumber={lambdaCheckTaxNumber}
        onBlurInput={handleSaveActivity}
        onBlurPhonenumber={handlePhoneValidationBlur}
        onChangeData={handleChangeData}
        onChangeBusinessId={handleChangeBusinessId}
        onChangeEmail={props.onSetEmail}
        onChangePhone={props.onSetPhone}
        passwordHelper={passwordHelper(props.t)}
        password2Helper={password2Helper(props.t)}
        register={register}
        submitEnabled={formValid}
        togglePhoneAccount={togglePhoneAccount}
        foundByBusinessId={foundByBusinessId}
        validCompany={validCompany}
        companyData={companyData}
        handleBossCheckbox={handleBossCheckbox}
        handleDateOfBirthYearOnBlur={handleDateOfBirthYearOnBlur}
        handleBusinessIdCountryRadioChange={handleBusinessIdCountryRadioChange}
        businessIdCountry={businessIdCountry}
        taxNumberLoading={taxNumberLoading}
      />
    );
  } else if (userConfirmed) {
    content = <h3>Registration successful, {cognitoUser.getUsername()}</h3>;
  } else {
    props.setVerificationView({
      projectKey: registrationData.projectKey.value,
      activityId: activityId,
    });
  }

  return content;
}

RegistrationLogic.propTypes = {
  t: PropTypes.func.isRequired,
  setVerificationView: PropTypes.func.isRequired,
  onSetEmail: PropTypes.func.isRequired,
  onSetPhone: PropTypes.func.isRequired,
  onSetIsPhoneAccount: PropTypes.func.isRequired,
  email: PropTypes.string.isRequired,
  phone: PropTypes.string.isRequired,
  isPhoneAccount: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => {
  return {
    setVerificationView: PropTypes.func.isRequired,
    email: state.user.email,
    phone: state.user.phone,
    isPhoneAccount: state.user.isPhoneAccount,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setVerificationView: (data) => dispatch(setRoute(ROUTE.VERIFICATION, data)),
    onSetEmail: (email) => dispatch(setEmail(email)),
    onSetPhone: (phone) => dispatch(setPhone(phone)),
    onSetIsPhoneAccount: (isPhoneAccount) =>
      dispatch(setIsPhoneAccount(isPhoneAccount)),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(RegistrationLogic));
export { RegistrationLogic };
